import { useCallback, useState } from 'react';
import {
  AppNotification,
  getAllNotifications,
  markNotificationAsRead,
} from '../api/notifications';
import usePolling from './usePolling';

export default function useNotifications(enabled = true) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const response = await getAllNotifications();
      setNotifications(response.data ?? []);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  const markAsRead = useCallback(async (id: number) => {
    await markNotificationAsRead(id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
    );
  }, []);

  usePolling(fetchNotifications, {
    initialInterval: 30_000,
    maxInterval: 60_000,
    enabled,
  });

  const unread = notifications.filter((n) => !n.isRead);
  const read = notifications.filter((n) => n.isRead);

  return {
    notifications,
    unread,
    read,
    unreadCount: unread.length,
    loading,
    refresh: fetchNotifications,
    markAsRead,
  };
}

export function resolveNotificationLink(linkUrl?: string | null): string | undefined {
  if (!linkUrl) return undefined;
  if (linkUrl.startsWith('/admin')) return linkUrl;
  if (linkUrl.startsWith('/purchase-orders')) return '/admin/purchase-orders';
  if (linkUrl.startsWith('/')) return `/admin${linkUrl}`;
  return linkUrl;
}
